import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AlertTriangle } from "lucide-react";
import { ACTION_BUTTON, plural } from "../styles";
import { BulkFailureType, PendingActionType } from "../types";

const MAX_VISIBLE_ITEMS = 10;

type ConfirmDialogProps = {
  pendingAction: PendingActionType | null;
  isProcessing: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

const buildTitle = ({ action, scope }: PendingActionType) => {
  const verb = action === "accept" ? "Aceitar" : "Rejeitar";

  if (scope === "single") return `${verb} distribuição`;
  if (scope === "contest") return `${verb} todas as pendências do concurso`;

  return `${verb} distribuições selecionadas`;
};

export const ConfirmDialog = ({
  pendingAction,
  isProcessing,
  onCancel,
  onConfirm,
}: ConfirmDialogProps) => {
  const items = pendingAction?.items ?? [];
  const visibleItems = items.slice(0, MAX_VISIBLE_ITEMS);
  const hiddenCount = items.length - visibleItems.length;
  const totalQuotas = items.reduce((total, item) => total + (item.quotas ?? 0), 0);
  const isAccept = pendingAction?.action === "accept";

  return (
    <Dialog
      open={Boolean(pendingAction)}
      onOpenChange={(open) => {
        if (!open && !isProcessing) onCancel();
      }}
    >
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>
            {pendingAction ? buildTitle(pendingAction) : ""}
          </DialogTitle>
          <DialogDescription>
            {items.length === 1
              ? "Confirme a distribuição abaixo."
              : `Confirme as ${items.length} distribuições abaixo.`}{" "}
            Total de <strong>{totalQuotas} cotas</strong>
            {pendingAction?.scope === "contest" && pendingAction.contestName
              ? ` no concurso ${pendingAction.contestName}`
              : ""}
            .
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-72 space-y-1 overflow-y-auto rounded-lg border border-border bg-muted/40 p-3">
          {visibleItems.map((item) => (
            <p key={item.uuid} className="text-sm text-foreground">
              • {item.name} - {item.classification}{" "}
              <span className="text-muted-foreground">
                ({item.quotas} {plural(item.quotas, "cota", "cotas")})
              </span>
            </p>
          ))}
          {hiddenCount > 0 && (
            <p className="pt-1 text-sm font-medium text-muted-foreground">
              e mais {hiddenCount}...
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isProcessing}>
            Cancelar
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isProcessing}
            className={
              isAccept ? ACTION_BUTTON.acceptSolid : ACTION_BUTTON.rejectSolid
            }
          >
            {isProcessing ? (
              <>
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-current" />
                Processando...
              </>
            ) : isAccept ? (
              "Confirmar aceitação"
            ) : (
              "Confirmar rejeição"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

type BulkResultDialogProps = {
  failures: BulkFailureType[] | null;
  processedCount: number;
  onClose: () => void;
};

/** Detalha as falhas de um lote parcialmente processado. */
export const BulkResultDialog = ({
  failures,
  processedCount,
  onClose,
}: BulkResultDialogProps) => (
  <Dialog
    open={Boolean(failures && failures.length > 0)}
    onOpenChange={(open) => {
      if (!open) onClose();
    }}
  >
    <DialogContent className="sm:max-w-[560px]">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          Lote processado parcialmente
        </DialogTitle>
        <DialogDescription>
          {processedCount} distribuição{processedCount === 1 ? "" : "ões"}{" "}
          processada{processedCount === 1 ? "" : "s"} com sucesso.{" "}
          {failures?.length} não {failures?.length === 1 ? "pôde" : "puderam"}{" "}
          ser processada{failures?.length === 1 ? "" : "s"}:
        </DialogDescription>
      </DialogHeader>

      <div className="max-h-72 space-y-2 overflow-y-auto rounded-lg border border-border bg-muted/40 p-3">
        {failures?.map((failure) => (
          <div key={failure.uuid} className="text-sm">
            <p className="font-medium text-foreground">{failure.label}</p>
            <p className="text-rose-700 dark:text-rose-300">{failure.error}</p>
          </div>
        ))}
      </div>

      <DialogFooter>
        <Button onClick={onClose}>Entendi</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
